import dayjs, { type Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { PERIOD_PRESET_OPTIONS } from '../constants/period.constant';
import type {
  DateRangeType,
  PeriodGranularityType,
  PeriodPresetOptionType,
  PeriodReferentialIntervalType
} from '../types/period.type';

dayjs.extend(isoWeek);

const MONTHS_IN_ROLLING_YEAR = 12;

/**
 * Presets the UI may offer for a granularity.
 */
export function getAvailablePresets(granularity: PeriodGranularityType): PeriodPresetOptionType[] {
  // A "previous day" preset makes no sense: the day is already the picker's own unit
  return PERIOD_PRESET_OPTIONS.filter((option) => granularity !== 'daily' || option !== 'last_period');
}

/**
 * Whether a preset makes sense for a granularity, used to reject stale saved values.
 */
export function isPresetAvailable(option: PeriodPresetOptionType, granularity: PeriodGranularityType): boolean {
  return getAvailablePresets(granularity).includes(option);
}

/**
 * Snaps both bounds of a range to whole units of the granularity (day, ISO week or month).
 */
export function normalizeRangeToGranularity(
  { start, end }: DateRangeType,
  granularity: PeriodGranularityType
): DateRangeType {
  switch (granularity) {
    case 'weekly':
      return { start: start.startOf('isoWeek'), end: end.endOf('isoWeek') };
    case 'monthly':
      return { start: start.startOf('month'), end: end.endOf('month') };
    default:
      return { start: start.startOf('day'), end: end.endOf('day') };
  }
}

/**
 * The last complete unit of the refresh state: J-1, S-1 or M-1.
 */
export function getDefaultCustomRange(
  granularity: PeriodGranularityType,
  interval: PeriodReferentialIntervalType
): DateRangeType {
  return normalizeRangeToGranularity({ start: interval.end, end: interval.end }, granularity);
}

/**
 * Turns a preset key into real dates, anchored on the last complete unit of the refresh state.
 */
export function computePresetRange(
  option: PeriodPresetOptionType,
  granularity: PeriodGranularityType,
  interval: PeriodReferentialIntervalType
): DateRangeType {
  const lastUnit = getDefaultCustomRange(granularity, interval);

  switch (option) {
    case 'last_period':
      return lastUnit;
    case 'year_to_date':
      return { start: getYearStart(lastUnit.end, granularity), end: lastUnit.end };
    case 'cumulative_to_date':
      return { start: getRollingYearStart(lastUnit, granularity), end: lastUnit.end };
  }
}

function getYearStart(end: Dayjs, granularity: PeriodGranularityType): Dayjs {
  if (granularity === 'weekly') {
    // ISO week 1 of the ISO year of S-1 may start in December of the previous calendar year
    return end.isoWeek(1).startOf('isoWeek');
  }

  return end.startOf('year');
}

function getRollingYearStart({ start, end }: DateRangeType, granularity: PeriodGranularityType): Dayjs {
  switch (granularity) {
    case 'weekly':
      // Same rule as the legacy selector: from the ISO week of the same date one year earlier, included,
      // so S-1 = W37 2026 starts at W37 2025 (53 weeks, not a strict 52-week rolling year)
      return end.subtract(1, 'year').startOf('isoWeek');
    case 'monthly':
      return start.subtract(MONTHS_IN_ROLLING_YEAR - 1, 'month');
    default:
      return end.startOf('day').subtract(1, 'year').add(1, 'day');
  }
}
